import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { ApiError } from '@/lib/problem-details'

import { TellerProblem } from './teller-problem'

/**
 * The shell every teller command dialog is built in.
 *
 * It has two faces. Before the click it is a form, with the dual-control notice above the fields
 * where the command is countersigned. After the command answers it stops being a form and shows
 * what happened instead, because "sent for countersignature" is a different message from "done" and
 * an operator who watches a dialog vanish reads that as "done" whatever a toast says afterwards.
 *
 * A command that takes effect immediately — opening a session, recording a count, posting a
 * deposit — can pass an outcome too. At a cash counter the receipt matters even when nothing was
 * deferred: the teller reads the figure back to the customer off this panel.
 */
export interface TellerActionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: ReactNode
  /** Rendered above the fields — the dual-control notice belongs here. */
  notice?: ReactNode
  children: ReactNode
  /** Present once the command has answered. Replaces the form. */
  outcome?: ReactNode
  confirmLabel: string
  closeLabel?: string
  tone?: 'default' | 'danger'
  isPending?: boolean
  /** True while the form is incomplete. */
  disabled?: boolean
  error?: ApiError | null
  onConfirm: () => void
  /** Wider than the default, for the count sheet. */
  size?: 'default' | 'wide'
}

export function TellerActionDialog({
  open,
  onOpenChange,
  title,
  description,
  notice,
  children,
  outcome,
  confirmLabel,
  closeLabel = 'Close',
  tone = 'default',
  isPending = false,
  disabled = false,
  error = null,
  onConfirm,
  size = 'default',
}: TellerActionDialogProps) {
  const settled = outcome !== undefined && outcome !== null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={size === 'wide' ? 'max-w-2xl' : undefined}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {settled ? (
            outcome
          ) : (
            <>
              {notice}
              {children}
              {error ? <TellerProblem error={error} /> : null}
            </>
          )}
        </DialogBody>

        <DialogFooter>
          {settled ? (
            <Button variant="primary" onClick={() => onOpenChange(false)}>
              {closeLabel}
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
                Cancel
              </Button>
              <Button
                variant={tone === 'danger' ? 'danger' : 'primary'}
                onClick={onConfirm}
                disabled={isPending || disabled}
              >
                {isPending ? 'Working…' : confirmLabel}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
